import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import PortalSnackbar from '../Common/PortalSnackbar';
import {
  ArrowLeft,
  Gear,
  Link as LinkIcon
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../context/BusinessContext';
import { useBusinessSubscription } from '../../hooks/useBusinessSubscription';
import UpgradeCTA from '../Business/UpgradeCTA';
import ServiceTypeManagement from './ServiceTypeManagement';
import CalendarSyncSettings from './CalendarSyncSettings';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

function BookingSettings() {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const { planKey } = useBusinessSubscription();
  const isFreePlan = planKey === 'supplier_free';
  const [loading, setLoading] = useState(false);
  const [calendarSettings, setCalendarSettings] = useState({
    startOfWeek: 0, // 0 = Sunday, 1 = Monday, etc.
    visibleUsers: [],
    syncEnabled: false
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (selectedBusiness?.business?._id) {
      fetchCalendarSettings();
      fetchTeamMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedBusiness?.business?._id]);

  const fetchCalendarSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/business/${selectedBusiness.business._id}/calendar-settings`);
      const data = response.data || {};
      setCalendarSettings({
        startOfWeek: data.startOfWeek ?? 0,
        visibleUsers: Array.isArray(data.visibleUsers) ? data.visibleUsers : [],
        syncEnabled: data.syncEnabled ?? false
      });
    } catch (error) {
      setCalendarSettings({
        startOfWeek: 0,
        visibleUsers: [],
        syncEnabled: false
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get(`/api/supplier/staff-members?businessId=${selectedBusiness?.business?._id}`);
      const staffMembers = response.data.staffMembers || [];
      
      // Transform staff members to match expected format
      const members = staffMembers
        .filter(sm => sm.status === 'active')
        .map(sm => {
          const _displayName = sm.user 
            ? `${sm.user.firstName || ''} ${sm.user.lastName || ''}`.trim() || sm.name
            : sm.name;
          // Use BusinessMember ID for booking assignment (bookings reference BusinessMember)
          const memberId = sm.businessMember?._id || sm.businessMember || sm._id;
          
          return {
            _id: memberId,
            user: sm.user || {
              firstName: sm.name.split(' ')[0] || '',
              lastName: sm.name.split(' ').slice(1).join(' ') || ''
            },
            role: sm.role || 'Staff'
          };
        });
      
      setTeamMembers(members);
    } catch (error) {
      setTeamMembers([]);
    }
  };

  const handleSaveCalendarSettings = async () => {
    try {
      setLoading(true);
      await api.put(`/api/business/${selectedBusiness.business._id}/calendar-settings`, calendarSettings);
      setSnackbar({
        open: true,
        message: 'Calendar settings saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to save calendar settings',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };


  if (loading && teamMembers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowLeft size={20} />}
          onClick={() => navigate('/supplier/bookings')}
          sx={{ mb: 2 }}
        >
          Back to Bookings
        </Button>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          Booking Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your appointment types and calendar preferences
        </Typography>
      </Box>

      {/* Appointment Types Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define the services couples can book with you. Set durations, pricing, which team members can deliver each one, and whether it's in-person or virtual.
          </Typography>
          <ServiceTypeManagement />
        </CardContent>
      </Card>

      {/* Calendar Settings & Sync Calendar - Side by side */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Gear size={24} />
                Calendar Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose the first day of your week and which team members appear on the calendar.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
              {/* Start of Week */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Start of Week</InputLabel>
                <Select
                  value={calendarSettings.startOfWeek}
                  label="Start of Week"
                  onChange={(e) => setCalendarSettings({
                    ...calendarSettings,
                    startOfWeek: e.target.value
                  })}
                >
                  <MenuItem value={0}>Sunday</MenuItem>
                  <MenuItem value={1}>Monday</MenuItem>
                  <MenuItem value={2}>Tuesday</MenuItem>
                  <MenuItem value={3}>Wednesday</MenuItem>
                  <MenuItem value={4}>Thursday</MenuItem>
                  <MenuItem value={5}>Friday</MenuItem>
                  <MenuItem value={6}>Saturday</MenuItem>
                </Select>
              </FormControl>

              {/* Visible Users */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel shrink>Which Users to View</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={calendarSettings.visibleUsers ?? []}
                  label="Which Users to View"
                  onChange={(e) => setCalendarSettings({
                    ...calendarSettings,
                    visibleUsers: e.target.value
                  })}
                  renderValue={(selected) => {
                    if (selected.length === 0) return 'All Users';
                    if (selected.length === teamMembers.length) return 'All Users';
                    return `${selected.length} user${selected.length > 1 ? 's' : ''} selected`;
                  }}
                >
                  <MenuItem value="all">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={calendarSettings.visibleUsers.length === teamMembers.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCalendarSettings({
                                ...calendarSettings,
                                visibleUsers: teamMembers.map(m => m._id)
                              });
                            } else {
                              setCalendarSettings({
                                ...calendarSettings,
                                visibleUsers: []
                              });
                            }
                          }}
                        />
                      }
                      label="All Users"
                    />
                  </MenuItem>
                  {teamMembers.map((member) => (
                    <MenuItem key={member._id} value={member._id}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={calendarSettings.visibleUsers.includes(member._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCalendarSettings({
                                  ...calendarSettings,
                                  visibleUsers: [...calendarSettings.visibleUsers, member._id]
                                });
                              } else {
                                setCalendarSettings({
                                  ...calendarSettings,
                                  visibleUsers: calendarSettings.visibleUsers.filter(id => id !== member._id)
                                });
                              }
                            }}
                          />
                        }
                        label={`${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || 'Unnamed User'}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Save Button */}
              <Button
                variant="contained"
                onClick={handleSaveCalendarSettings}
                disabled={loading}
                fullWidth
              >
                Save Calendar Settings
              </Button>
            </Grid>
          </Grid>
        </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
          <Typography variant="h6" gutterBottom sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon size={24} />
            Sync Calendar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            See your Idotoo bookings alongside your personal calendar. You'll get a link that plugs into Apple Calendar, Outlook, or any app that supports calendar subscriptions.
          </Typography>

          {isFreePlan ? (
            <UpgradeCTA reason="calendar sync with your phone or computer" compact />
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<LinkIcon size={20} />}
                  onClick={() => setShowSyncSettings(true)}
                  fullWidth
                  sx={{
                    backgroundColor: '#E91E63',
                    '&:hover': {
                      backgroundColor: '#C2185B'
                    }
                  }}
                >
                  Sync Calendar
                </Button>
              </Grid>
            </Grid>
          )}
        </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Calendar Sync Settings Dialog */}
      {showSyncSettings && !isFreePlan && (
        <CalendarSyncSettings
          open={showSyncSettings}
          onClose={() => setShowSyncSettings(false)}
          businessId={selectedBusiness?.business?._id}
        />
      )}

      {/* Snackbar for notifications */}
      <PortalSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </PortalSnackbar>
    </Container>
  );
}

export default BookingSettings;
